/**
 * Automations — the list screen (replaces Performance Alerts).
 *
 * Every automation is a plain-English row: an on/off switch, the name, a
 * readable recipe line ("When … → notify the player and alert their manager"),
 * the workspace chip and a ⋯ menu (edit / duplicate / delete). "＋ New
 * automation" opens the centered builder modal.
 *
 * Embedded in Team Room (scoped to that workspace) and Company Setup
 * (all workspaces).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import LoadingScreen from "../../components/LoadingScreen";
import toast from "react-hot-toast";

import { UseCompanyColors, UseCompanyWorkspaces, UseSelectedWorkspace } from "../../hooks/company";
import {
  FETCH_AUTOMATIONS,
  CREATE_AUTOMATION,
  UPDATE_AUTOMATION,
  TOGGLE_AUTOMATION,
  DELETE_AUTOMATION,
  FETCH_AUTOMATION_RUNS,
} from "../../api/automations";

import BuilderModal from "./BuilderModal";
import brandTokens from "./brandTokens";
import { recipeLine, stepDefById } from "./catalog";
import useOutsideClick, { keyActivate } from "./useOutsideClick";
import "./automations.css";

// Who a run's channels actually went to — the log's subject name is who the
// automation fired ABOUT, not who was notified, so spell that out separately
// to avoid reading like "sent to {subjectName}".
const recipientPhrase = (automation) => {
  const labels = (automation?.steps || [])
    .filter((s) => s.kind === "action")
    .map((s) => stepDefById(s.type)?.recipient)
    .filter(Boolean);
  const unique = [...new Set(labels)];
  if (!unique.length) return null;
  return unique.map((l) => l.charAt(0).toLowerCase() + l.slice(1)).join(" and ");
};

const SearchIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const KEBAB_ICONS = {
  edit: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  ),
  duplicate: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  ),
  log: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" />
    </svg>
  ),
  trash: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" />
    </svg>
  ),
};

/** Status pill for one execution-log row. */
const RUN_STATUS = {
  sent: { bg: "#ECFDF3", color: "#067647", label: "Sent" },
  partial: { bg: "#FFFAEB", color: "#B54708", label: "Partially sent" },
  failed: { bg: "#FEF3F2", color: "#B42318", label: "Failed" },
  skipped: { bg: "#F2F4F7", color: "#475467", label: "Skipped" },
};

const runStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

// Rendered unconditionally before pagination existed — fine for a handful of
// automations, but a company with a lot of them was paying for every single
// row's DOM (and its own kebab-menu markup) on every render.
const PAGE_SIZE = 20;

/** "Updated yesterday"-style stamp, kept rough on purpose. */
const updatedAgo = (iso) => {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "Updated today";
  if (days === 1) return "Updated yesterday";
  if (days < 7) return `Updated ${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `Updated ${weeks} week${weeks === 1 ? "" : "s"} ago`;
  return `Updated ${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? "" : "s"} ago`;
};

export default function Automations({ workspaceId } = {}) {
  const { primaryColor } = UseCompanyColors() || {};
  const primary = primaryColor || "#1B598E";
  const workspaces = UseCompanyWorkspaces(true) || [];
  const selectedWorkspace = UseSelectedWorkspace();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "active" | "inactive"
  const [menuFor, setMenuFor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Execution log — per automation: which one is open + its runs (null = loading).
  const [logTarget, setLogTarget] = useState(null);
  const [logRuns, setLogRuns] = useState(null);
  const [logRunsError, setLogRunsError] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [retryTick, setRetryTick] = useState(0);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    setLoading(true);
    setLoadError(false);
    FETCH_AUTOMATIONS(workspaceId)
      .then(({ rows, recoveredCount }) => {
        if (!aliveRef.current) return;
        setItems(Array.isArray(rows) ? rows : []);
        if (recoveredCount > 0) {
          toast.success(
            `Recovered ${recoveredCount} automation${recoveredCount === 1 ? "" : "s"} that ${
              recoveredCount === 1 ? "was" : "were"
            } only saved in your browser.`
          );
        }
      })
      .catch((error) => {
        if (!aliveRef.current) return;
        console.error("Automations: failed to load the list:", error?.message || error);
        setItems([]);
        setLoadError(true);
      })
      .finally(() => aliveRef.current && setLoading(false));
    return () => {
      aliveRef.current = false;
    };
  }, [workspaceId, retryTick]);

  // Close any open row menu on outside click (or Escape).
  useOutsideClick(!!menuFor, () => setMenuFor(null));

  const workspaceName = (gid) => workspaces.find((w) => String(w._id) === String(gid))?.name || "";
  const builderGroupId = workspaceId || selectedWorkspace?._id;
  const builderWorkspaceName = workspaceId ? workspaceName(workspaceId) : selectedWorkspace?.name || "";

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((a) => {
      if (needle && !(a.name || "").toLowerCase().includes(needle)) return false;
      const active = a.active !== false;
      if (statusFilter === "active" && !active) return false;
      if (statusFilter === "inactive" && active) return false;
      return true;
    });
  }, [items, search, statusFilter]);

  // A new search/filter should start back at page one — otherwise narrowing
  // to 3 matches while scrolled to row 80 would just show an empty page.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, statusFilter, workspaceId]);

  const pageItems = visible.slice(0, visibleCount);
  const remaining = visible.length - pageItems.length;

  const openCreate = () => {
    setEditing(null);
    setBuilderOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setBuilderOpen(true);
  };

  const handleSave = async (payload, isNew) => {
    if (isNew) {
      const saved = await CREATE_AUTOMATION({ ...payload, groupId: editing?.groupId || builderGroupId });
      setItems((list) => [saved, ...list]);
      return saved;
    }
    const saved = await UPDATE_AUTOMATION(editing._id, payload);
    setItems((list) => list.map((a) => (a._id === saved._id ? saved : a)));
    return saved;
  };

  const handleToggle = (a) => {
    if (togglingIds.has(a._id)) return; // a request for this row is already in flight
    const next = !(a.active !== false);
    setTogglingIds((ids) => new Set(ids).add(a._id));
    setItems((list) => list.map((x) => (x._id === a._id ? { ...x, active: next } : x)));
    TOGGLE_AUTOMATION(a._id, next)
      .catch(() => {
        setItems((list) => list.map((x) => (x._id === a._id ? { ...x, active: !next } : x)));
        toast.error("Could not update the automation.");
      })
      .finally(() => {
        setTogglingIds((ids) => {
          const next2 = new Set(ids);
          next2.delete(a._id);
          return next2;
        });
      });
  };

  // Open one automation's execution log and fetch its runs.
  const openLog = (a) => {
    setLogTarget(a);
    setLogRuns(null);
    setLogRunsError(false);
    FETCH_AUTOMATION_RUNS(a._id)
      .then((rows) => setLogRuns(rows))
      .catch((error) => {
        console.error("Automations: failed to load the execution log:", error?.message || error);
        setLogRuns([]);
        setLogRunsError(true);
      });
  };

  const handleDuplicate = async (a) => {
    try {
      const saved = await CREATE_AUTOMATION({
        groupId: a.groupId,
        name: `${a.name} (copy)`,
        active: false,
        trigger: a.trigger,
        steps: a.steps,
      });
      setItems((list) => [saved, ...list]);
      toast.success("Duplicated — it starts turned off.");
    } catch {
      toast.error("Could not duplicate the automation.");
    }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      await DELETE_AUTOMATION(target._id);
      setItems((list) => list.filter((x) => x._id !== target._id));
      toast.success("Automation deleted.");
    } catch {
      toast.error("Could not delete the automation.");
    }
  };

  const recipe = (a) => {
    const { when, actions } = recipeLine(a);
    return (
      <>
        <b>When</b> {when.toLowerCase()}
        {actions.length > 0 && (
          <>
            {" → "}
            {actions.map((label, i) => (
              <React.Fragment key={label + String(i)}>
                {i > 0 && (i === actions.length - 1 ? " and " : ", ")}
                <b>{label.toLowerCase()}</b>
              </React.Fragment>
            ))}
          </>
        )}
        .
      </>
    );
  };

  return (
    <div className="gmba" style={{ ...brandTokens(primary) }}>
      {/* toolbar */}
      <div className="gmba-toolbar">
        <span className="gmba-search">
          {SearchIcon}
          <input placeholder="Search automations" value={search} onChange={(e) => setSearch(e.target.value)} />
        </span>
        <div className="gmba-filters">
          {[
            { key: "all", label: "All" },
            { key: "active", label: "Active" },
            { key: "inactive", label: "Inactive" },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              className={`gmba-filterbtn${statusFilter === f.key ? " gmba-filterbtnon" : ""}`}
              onClick={() => setStatusFilter(f.key)}
            >{f.label}</button>
          ))}
        </div>
        <span className="gmba-spacer" style={{ flex: 1 }} />
        <button type="button" className="gmba-new" onClick={openCreate}>＋ New automation</button>
      </div>

      {loadError && !loading && (
        <div className="gmba-errorbar">
          Couldn't load your automations — this isn't the same as having none.
          <button type="button" onClick={() => setRetryTick((t) => t + 1)}>Retry</button>
        </div>
      )}

      {loading && <LoadingScreen height="220px" logoHeight="120px" />}

      {!loading && !loadError && !visible.length && (
        <div className="gmba-empty">
          <div className="gmba-empty-t">
            {search || statusFilter !== "all" ? "No automations match that." : "Nothing runs itself yet"}
          </div>
          <div className="gmba-empty-d">
            {search || statusFilter !== "all"
              ? "Try a different search or filter."
              : "Automations are little rules that work for you — alerts when someone falls behind, birthday shout-outs, rewards when targets are hit. Build one in about a minute."}
          </div>
          {!search && statusFilter === "all" && (
            <button type="button" className="gmba-new" onClick={openCreate}>＋ Build your first automation</button>
          )}
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="gmba-list">
          {pageItems.map((a) => {
            const active = a.active !== false;
            const toggling = togglingIds.has(a._id);
            return (
              <div key={a._id} className={`gmba-row${active ? "" : " gmba-off"}`}>
                <button
                  type="button"
                  className={`gmba-sw ${active ? "gmba-on" : "gmba-offsw"}`}
                  onClick={() => handleToggle(a)}
                  disabled={toggling}
                  style={toggling ? { opacity: 0.6, cursor: "default" } : undefined}
                  aria-label={active ? "Turn off" : "Turn on"}
                />
                <div className="gmba-row-main">
                  <div className="gmba-row-name">{a.name}</div>
                  <div className="gmba-row-recipe">{recipe(a)}</div>
                </div>
                <div className="gmba-row-meta">
                  {!workspaceId && workspaceName(a.groupId) && (
                    <span className="gmba-chip">{workspaceName(a.groupId)}</span>
                  )}
                  <span className="gmba-upd">{updatedAgo(a.updatedAt)}</span>
                  <span style={{ position: "relative" }}>
                    <button
                      type="button"
                      className="gmba-kebab"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuFor(menuFor === a._id ? null : a._id);
                      }}
                    >⋯</button>
                    {menuFor === a._id && (
                      <div className="gmba-menu" style={{ right: 0, top: 28 }} onClick={(e) => e.stopPropagation()}>
                        {[
                          { label: "Edit", icon: KEBAB_ICONS.edit, onClick: () => openEdit(a) },
                          { label: "Duplicate", icon: KEBAB_ICONS.duplicate, onClick: () => handleDuplicate(a) },
                          { label: "Execution log", icon: KEBAB_ICONS.log, onClick: () => openLog(a) },
                        ].map((item) => {
                          const activate = () => { setMenuFor(null); item.onClick(); };
                          return (
                            <div key={item.label} className="gmba-mi" onClick={activate} {...keyActivate(activate)}>
                              {item.icon}{item.label}
                            </div>
                          );
                        })}
                        <div className="gmba-mdiv" />
                        {(() => {
                          const activate = () => { setMenuFor(null); setDeleteTarget(a); };
                          return (
                            <div className="gmba-mi gmba-danger" onClick={activate} {...keyActivate(activate)}>
                              {KEBAB_ICONS.trash}Delete
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && remaining > 0 && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button type="button" className="gmba-addstep" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
            Show {Math.min(remaining, PAGE_SIZE)} more ({remaining} left)
          </button>
        </div>
      )}

      {/* delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <div className="gmba gmba-success" style={{ ...brandTokens(primary), width: 360, padding: "28px 28px 22px" }}>
          <div className="gmba-success-t" style={{ fontSize: 17 }}>Delete “{deleteTarget?.name}”?</div>
          <div className="gmba-success-d">It stops running immediately. This can't be undone.</div>
          <div className="gmba-success-row" style={{ marginTop: 20 }}>
            <button type="button" className="gmba-btn gmba-ghost" onClick={() => setDeleteTarget(null)}>Keep it</button>
            <button
              type="button"
              className="gmba-btn gmba-primary"
              style={{ background: "#B42318", borderColor: "#B42318" }}
              onClick={handleDelete}
            >Delete</button>
          </div>
        </div>
      </Dialog>

      {/* execution log — per automation, opened from its ⋯ menu */}
      <Dialog open={!!logTarget} onClose={() => setLogTarget(null)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <div className="gmba" style={{ ...brandTokens(primary), width: 420, maxWidth: "92vw", padding: "20px 22px 16px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontFamily: "DM Sans", fontSize: 16, fontWeight: 600, color: "#101828" }}>Execution log</div>
              <div style={{ fontFamily: "DM Sans", fontSize: 12, color: "#667085", marginTop: 2 }}>{logTarget?.name}</div>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setLogTarget(null)}
              style={{ width: 26, height: 26, borderRadius: "50%", border: 0, cursor: "pointer", background: "#F2F4F7", color: "#667085", fontSize: 13, lineHeight: 1 }}
            >✕</button>
          </div>

          <div style={{ marginTop: 12, minHeight: 120 }}>
            {logRuns === null && <LoadingScreen height="120px" logoHeight="70px" />}
            {logRunsError && (
              <div className="gmba-errorbar" style={{ marginBottom: 0 }}>
                Couldn't load the execution log.
                <button type="button" onClick={() => openLog(logTarget)}>Retry</button>
              </div>
            )}
            {!logRunsError && Array.isArray(logRuns) && logRuns.length === 0 && (
              <div style={{ textAlign: "center", padding: "26px 12px", fontFamily: "DM Sans" }}>
                <div style={{ fontSize: 13.5, fontWeight: 500, color: "#344054" }}>No runs yet</div>
                <div style={{ fontSize: 12, color: "#98A2B3", marginTop: 4, lineHeight: 1.5 }}>
                  Every time this automation fires it's recorded here — who it was about, what was sent and whether it landed.
                </div>
              </div>
            )}
            {!logRunsError && Array.isArray(logRuns) &&
              (() => {
                // Same for every row — this automation's recipients don't
                // change per run, only who each run was about does.
                const recipients = recipientPhrase(logTarget);
                return logRuns.map((r, i) => {
                  const s = RUN_STATUS[r.status] || RUN_STATUS.skipped;
                  const meta = [runStamp(r.createdAt || r.at), ...(Array.isArray(r.channels) ? r.channels : [])]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <div
                      key={r._id || i}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "9px 0", borderTop: "1px solid #EAECF0", fontFamily: "DM Sans" }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 500, color: "#191D23" }}>
                          About {r.subjectName || "the workspace"}
                          {recipients && (
                            <span style={{ fontWeight: 400, color: "#667085" }}> — notifies {recipients}</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: "#98A2B3", marginTop: 1 }}>{meta}</div>
                      </div>
                      <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 500, padding: "3px 9px", borderRadius: 999, background: s.bg, color: s.color }}>
                        {s.label}{r.status !== "sent" && r.reason ? ` — ${r.reason}` : ""}
                      </span>
                    </div>
                  );
                });
              })()}
          </div>
        </div>
      </Dialog>

      {/* builder */}
      <BuilderModal
        open={builderOpen}
        automation={editing}
        workspaceName={editing ? workspaceName(editing.groupId) : builderWorkspaceName}
        groupId={editing?.groupId || builderGroupId}
        onSave={handleSave}
        onClose={() => setBuilderOpen(false)}
        primary={primary}
      />
    </div>
  );
}
